import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { Spinner } from '@acorns/web-components'
import { withAnalyticsClient } from '@acorns/web-utils'
import {
  branch,
  compose,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import styled from 'styled-components'

import ConfirmationModal, {
  ModalAction,
  ModalActionTypes,
} from 'components/ConfirmationModal'
import FullscreenTakeover from 'components/FullscreenTakeover'
import withSpendClosedRedirect from 'components/HOCs/Spend/withSpendClosedRedirect'
import { FundingSource } from 'generated/types'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { withEdgeCaseProductInfo } from 'pages/settings/my-subscription/tier-subscription-center/utils'
import { style } from 'theme'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import LoadingScreen from '../closureLoading'
import cancelRecurringMutation from '../mutations/cancelSpendRecurring.gql'
import mutation from '../mutations/mutation.gql'
import spendInfoQuery from '../queries/spendQuery.gql'
import userQuery from '../queries/userQuery.gql'
import { CloseWithBalanceConfirmation } from './confirm-with-balance'
import { ConfirmationScreen } from './confirmation-screen'
import { RequestScreen } from './request-screen'

type HandlerProps = {
  handleCancel?: () => void
  handleDone?: () => void
  handleAcknowledgeDisclaimer?: () => void
  handleCheckboxChange?: () => void
  handleDoneClick?: () => void
  handleShowConfirmationModal?: () => void
}

type GqlProps = {
  spendBalance?: number
  spendRecurringTransferId?: string
  spendRecurringTransferStatus?: boolean
  userData?: object
}

type Props = {
  checked?: boolean
  closureItems?: string[]
  disabled?: boolean
  isWebView?: boolean
  fundingSourceForTransfers?: FundingSource
  modalActions?: ModalAction[]
  modalBody?: string
  modalHeader?: string
  showCloseAccountModal?: boolean
  showConfirmRequestScreen?: boolean
  showConfirmWithBalance?: boolean
  showLoadingScreen?: boolean
  showSuccessScreen?: boolean
  spendAsFundingSource?: boolean
  finalTransferAccount?: string
  setShowCloseAccountModal?: (showCloseAccountModal: boolean) => void
  setShowConfirmRequestScreen?: (showConfirmRequestScreen: boolean) => void
  setShowConfirmWithBalance?: (showConfirmWithBalance: boolean) => void
  setShowSuccessScreen?: (showSuccessScreen: boolean) => void
  setShowLoadingScreen?: (showLoadingScreen: boolean) => void
  handleCancelCloseAccountModal?: () => void
  handleCloseAccountConfirmation?: () => void
  handleConfirmBalanceTransfer?: () => void
}

export type MergedProps = HandlerProps & GqlProps & Props

const StyledContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    background-color: #ffffff;
  }
`

const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
`

const CLOSE_ACCOUNT_TEXT = 'Close account'
const CANCEL_TEXT = 'Never mind'
const DONE_TEXT = 'Done'

const getModalProps = (props: MergedProps) => {
  const modalActions: ModalAction[] = [
    {
      handler: props.handleCancelCloseAccountModal,
      label: CANCEL_TEXT,
      type: ModalActionTypes.secondary,
    },
    {
      handler: props.handleCloseAccountConfirmation,
      label: CLOSE_ACCOUNT_TEXT,
      type: ModalActionTypes.danger,
    },
  ]
  const modalBody =
    props.spendBalance > 0
      ? 'This will transfer all your money from Spend into your linked checking account and then close your Spend account. '
      : 'This will close your Spend account'

  const modalHeader = 'Are you sure?'

  return {
    modalActions,
    modalBody,
    modalHeader,
  }
}

const enhance = compose<MergedProps, {}>(
  withSpendClosedRedirect(),
  withRouter,
  withEdgeCaseProductInfo,
  withAnalyticsClient,
  graphql(userQuery),
  graphql(spendInfoQuery, {
    name: 'spendInfo',
  }),
  graphql(mutation, {
    name: 'requestCloseAccountMutation',
  }),
  graphql(cancelRecurringMutation, {
    name: 'cancelRecurringTransfers',
  }),
  withState('checked', 'setChecked', false),
  withState('showCloseAccountModal', 'setShowCloseAccountModal', false),
  withState('showLoadingScreen', 'setShowLoadingScreen', false),
  withState('showSuccessScreen', 'setShowSuccessScreen', false),
  withState('showConfirmRequestScreen', 'setShowConfirmRequestScreen', true),
  withState('showConfirmWithBalance', 'setShowConfirmWithBalance', false),
  withProps(() => ({
    isWebView: window.location.pathname.startsWith('/wv/'),
  })),
  withAnalyticsIdentity,
  withProps(({ location, userAccountState }) => {
    const isCancelSubscriptionFlow = location?.state?.isCancelSubscriptionFlow
    const isDowngradeSubscriptionFlow =
      location?.state?.isDowngradeSubscriptionFlow
    const hasLaterAccount = !!userAccountState?.laterAccount
    const hasLaterBalance = userAccountState?.laterAccount?.balance

    return {
      hasLaterAccount,
      hasLaterBalance,
      isCancelSubscriptionFlow,
      isDowngradeSubscriptionFlow,
    }
  }),
  withProps(({ data }) => {
    const userInfo = data
    const userData = userInfo?.user || undefined
    const spendAsFundingSource =
      userInfo?.user?.fundingSource?.fundingType === 'spend'

    // if spend is primary funding source, we will need to identify the secondary FS that their balance will transfer to. There are a few edge cases where the role of 'secondaryFunding' may not be populated, hence the extra logic
    const findItem = (linkedAccount) =>
      linkedAccount?.linkedSubaccounts[0]?.role === 'seconaryFunding' ||
      (linkedAccount?.linkedSubaccounts[0]?.externalSource !== 'spend' &&
        linkedAccount?.linkedSubaccounts[0]?.allowedRoles.includes(
          'primaryFunding',
        ))

    const fundingSourceForTransfers = spendAsFundingSource
      ? userInfo?.linkedAccounts?.find(findItem)?.linkedSubaccounts?.[0]
      : userData?.fundingSource

    return {
      spendAsFundingSource,
      fundingSourceForTransfers,
      userData,
    }
  }),
  withProps(({ fundingSourceForTransfers, spendAsFundingSource }) => ({
    finalTransferAccount: `${fundingSourceForTransfers?.name} ${
      spendAsFundingSource
        ? fundingSourceForTransfers?.accountNumberLastFour
        : fundingSourceForTransfers?.last4
    }`,
  })),
  withProps(({ spendInfo }) => {
    if (!spendInfo.loading) {
      const spendBalance =
        spendInfo?.allBankAccounts?.[0]?.balance?.value || undefined

      const spendRecurringTransferStatus =
        spendInfo?.recurringTransfers?.[0]?.active
      const spendRecurringTransferId = spendInfo?.recurringTransfers?.[0]?.id

      return {
        spendBalance,
        spendRecurringTransferStatus,
        spendRecurringTransferId,
        loading: false,
      }
    }
    return {
      loading: true,
    }
  }),
  withProps(({ spendRecurringTransferStatus }) => {
    const closureItems = []
    if (spendRecurringTransferStatus) {
      closureItems.push('Turning off transfers', 'Requesting closure')
    }
    return {
      closureItems,
    }
  }),
  withHandlers({
    handleShowConfirmationModal:
      ({ setShowCloseAccountModal, analytics }) =>
      () => {
        setShowCloseAccountModal(true)
        analytics.track('Screen Viewed', {
          object_name: 'closeAccountSpendConfirmationModal',
          screen: 'closeAccountSpendConfirmationModal',
          screen_name: 'closeAccountSpendConfirmationModal',
        })
      },
  }),
  withHandlers({
    handleConfirmBalanceTransfer:
      ({ handleShowConfirmationModal, analytics, finalTransferAccount }) =>
      () => {
        analytics.track('Button Tapped', {
          object_name: 'closeAccountSpendConfirmationNext',
          screen: 'closeAccountSpendConfirmation',
          screen_name: 'closeAccountSpendConfirmation',
          to_account: finalTransferAccount,
        })
        handleShowConfirmationModal()
      },
  }),
  withHandlers({
    handleCancel:
      ({
        history,
        isWebView,
        analytics,
        showConfirmRequestScreen,
        showConfirmWithBalance,
        isDowngradeSubscriptionFlow,
        isCancelSubscriptionFlow,
      }) =>
      () => {
        // cancel link are on multiple screens, so this allows us to distinguish which screen that user has clicked from in the analytics events
        let screen
        if (showConfirmRequestScreen) {
          screen = 'closeAccountSpend'
        } else if (showConfirmWithBalance) {
          screen = 'closeAccountSpendConfirmation'
        }
        analytics.track('Button Tapped', {
          object_name: `${screen}Cancel`,
          screen,
          screen_name: screen,
        })
        const URL = () => {
          if (isDowngradeSubscriptionFlow) {
            return isWebView
              ? '/wv/my-subscription/downgrade'
              : '/settings/my-subscription/downgrade'
          } else if (isCancelSubscriptionFlow) {
            return isWebView
              ? '/wv/cancel-subscription'
              : '/cancel-subscription'
          } else {
            return isWebView
              ? '/wv/my-subscription'
              : '/settings/my-subscription'
          }
        }
        history.push(URL())
      },
    handleCheckboxChange:
      ({ setChecked, checked }) =>
      () => {
        setChecked(!checked)
      },
    handleAcknowledgeDisclaimer:
      ({
        analytics,
        handleShowConfirmationModal,
        hasLaterAccount,
        history,
        isCancelSubscriptionFlow,
        isDowngradeSubscriptionFlow,
        isWebView,
        setShowConfirmRequestScreen,
        setShowConfirmWithBalance,
        spendBalance,
        hasLaterBalance,
      }) =>
      () => {
        analytics.track('Button Tapped', {
          object_name: 'closeAccountSpendNext',
          screen: 'closeAccountSpend',
          screen_name: 'closeAccountSpend',
        })
        if (isCancelSubscriptionFlow) {
          const url = isWebView
            ? '/wv/close-account/invest'
            : '/close-account/invest'
          history.push(url, {
            referrer: location.pathname,
            isCancelSubscriptionFlow: true,
            hasLaterAccount,
            hasSpendAccount: true,
            hasLaterBalance,
          })
        } else if (isDowngradeSubscriptionFlow) {
          if (hasLaterBalance) {
            const url = isWebView
              ? '/wv/close-account/later'
              : '/close-account/later'
            history.push(url, {
              referrer: location.pathname,
              isDowngradeSubscriptionFlow: true,
              hasLaterAccount,
              hasSpendAccount: true,
            })
          } else {
            const url = isWebView
              ? '/wv/my-subscription/downgrade'
              : '/settings/my-subscription/downgrade'

            history.push(url, {
              referrer: location.pathname,
              isDowngradeSubscriptionFlow: true,
              shouldShowClosureConfirmationScreen: true,
              hasLaterAccount,
              hasSpendAccount: true,
              hasLaterBalance: false,
            })
          }
        } else {
          if (spendBalance > 0) {
            setShowConfirmWithBalance(true)
            setShowConfirmRequestScreen(false)
            analytics.track('Screen Viewed', {
              object_name: 'closeAccountSpendConfirmation',
              screen: 'closeAccountSpendConfirmation',
              screen_name: 'closeAccountSpendConfirmation',
            })
          } else {
            handleShowConfirmationModal()
          }
        }
      },
    handleCancelCloseAccountModal:
      ({ setShowCloseAccountModal, analytics }) =>
      () => {
        setShowCloseAccountModal(false)
        analytics.track('Button Tapped', {
          object_name: 'closeAccountSpendConfirmationModal',
          screen: 'closeAccountSpendConfirmationModal',
          screen_name: 'closeAccountSpendConfirmationModal',
          cta_title: CANCEL_TEXT,
        })
      },

    handleCloseAccountConfirmation:
      ({
        spendRecurringTransferId,
        requestCloseAccountMutation,
        spendRecurringTransferStatus,
        setShowLoadingScreen,
        setShowConfirmRequestScreen,
        setShowSuccessScreen,
        cancelRecurringTransfers,
        setShowCloseAccountModal,
        setShowConfirmWithBalance,
        showConfirmWithBalance,
        userData,
        analytics,
        userAccountState,
      }) =>
      () => {
        analytics.track('Button Tapped', {
          object_name: 'closeAccountSpendConfirmationModal',
          screen: 'closeAccountSpendConfirmationModal',
          screen_name: 'closeAccountSpendConfirmationModal',
          cta_title: CLOSE_ACCOUNT_TEXT,
        })

        const spendAccountMessage =
          userAccountState?.spendAccount?.active &&
          userAccountState?.spendAccount?.bankAccountStatus !== open
            ? '--please cancel pending Spend card order.'
            : ''

        const input = {
          category: 'Close My Account',
          uuid: userData.uuid,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          message: `Requesting closure of my Spend account${spendAccountMessage}.`,
          phone: userData.phoneNumber,
          subcategory: 'Close My Acorns Spend Account',
          tags: [
            'automatic_close_my_account',
            'automatic_close_my_acorns_spend_account',
          ],
        }
        setShowCloseAccountModal(false)

        showConfirmWithBalance
          ? setShowConfirmWithBalance(false)
          : setShowConfirmRequestScreen(false)

        setShowLoadingScreen(true)

        if (spendRecurringTransferStatus) {
          cancelRecurringTransfers({
            variables: { id: spendRecurringTransferId },
          })
        }

        requestCloseAccountMutation({
          variables: { input },
        }).then(() => {
          setShowLoadingScreen(false)
          setShowSuccessScreen(true)

          analytics.track('Screen Viewed', {
            object_name: 'closeAccountSpendDone',
            screen: 'closeAccountSpendDone',
            screen_name: 'closeAccountSpendDone',
          })
        })
      },
    handleDoneClick:
      ({ isWebView, history, location, analytics }) =>
      () => {
        analytics.track('Button Tapped', {
          object_name: 'closeAccountSpendDone',
          screen: 'closeAccountSpendDone',
          screen_name: 'closeAccountSpendDone',
          cta_title: DONE_TEXT,
        })
        if (isWebView) {
          window.location.assign('acorns://exit?navigate_to=present')
        } else {
          history.push('/present', {
            referrer: location.pathname,
          })
        }
      },
  }),
  withProps<any, any>((props) => getModalProps(props)),
  branch(
    ({ loading }) => loading,
    renderComponent(() => (
      <Loader>
        <Spinner />
      </Loader>
    )),
  ),
)

const CloseAccountSpend: React.FC<MergedProps> = ({
  checked,
  closureItems,
  isWebView,
  handleCancel,
  handleAcknowledgeDisclaimer,
  handleCheckboxChange,
  handleDoneClick,
  handleConfirmBalanceTransfer,
  spendBalance,
  showConfirmWithBalance,
  showConfirmRequestScreen,
  showLoadingScreen,
  showSuccessScreen,
  finalTransferAccount,
  isDowngradeSubscriptionFlow,
  isCancelSubscriptionFlow,
}) => {
  return (
    <>
      {!showSuccessScreen && !showLoadingScreen && (
        <CloseAccountWrapper
          isWebView={isWebView}
          action={{
            onClick:
              showConfirmRequestScreen || showConfirmWithBalance
                ? handleCancel
                : handleDoneClick,
            shouldShowCloseIcon:
              !showConfirmRequestScreen && !showConfirmWithBalance,
          }}
          link={{
            text:
              showConfirmRequestScreen || showConfirmWithBalance
                ? 'Cancel'
                : null,
            onClick:
              showConfirmRequestScreen || showConfirmWithBalance
                ? handleCancel
                : null,
          }}
          handleCancel={
            !showConfirmRequestScreen && !showConfirmWithBalance
              ? handleDoneClick
              : handleCancel
          }
          actionStripTitle={
            isDowngradeSubscriptionFlow
              ? 'Change subscription'
              : 'Close account'
          }
          showCancellable={showConfirmRequestScreen || showConfirmWithBalance}
          shouldNotShowBack={showSuccessScreen || showLoadingScreen}
          closeIcon={showSuccessScreen}
          showBack={showConfirmRequestScreen || showConfirmWithBalance}
        >
          {showConfirmRequestScreen && (
            <RequestScreen
              spendBalance={spendBalance}
              handleAcknowledgeDisclaimer={handleAcknowledgeDisclaimer}
              checked={checked}
              handleCheckboxChange={handleCheckboxChange}
              isMultiStepClosureFlow={
                isDowngradeSubscriptionFlow || isCancelSubscriptionFlow
              }
              isWebView={isWebView}
            />
          )}
          {showConfirmWithBalance && (
            <CloseWithBalanceConfirmation
              spendBalance={spendBalance}
              handleShowConfirmationModal={handleConfirmBalanceTransfer}
              finalTransferAccount={finalTransferAccount}
              isWebView={isWebView}
            />
          )}
        </CloseAccountWrapper>
      )}

      {showLoadingScreen && (
        <LoadingScreen
          closureItems={closureItems}
          closureHeading="Requesting account closure"
          isVisible={showLoadingScreen}
          isWebView={isWebView}
        />
      )}
      {showSuccessScreen && (
        <ConfirmationScreen
          hasSpendBalance={spendBalance > 0}
          handleDone={handleDoneClick}
          buttonText={DONE_TEXT}
          isWebView={isWebView}
        />
      )}
    </>
  )
}

const Page: React.FC<MergedProps> = (props) => {
  const { height } = useWindowDimensions()
  return (
    <>
      {props.isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountSpend {...props} />
        </FullscreenTakeover>
      )}
      {!props.isWebView && <CloseAccountSpend {...props} />}
      {props.showCloseAccountModal && (
        <ConfirmationModal
          actions={props.modalActions}
          body={props.modalBody}
          header={props.modalHeader}
          onRequestClose={() => props.setShowCloseAccountModal(false)}
          isOpen={props.showCloseAccountModal}
        />
      )}
    </>
  )
}
export default enhance(Page)
export { enhance, Page }
